import React, { useState } from 'react';
import { Calendar, Plus, Clock, CheckCircle2, AlertCircle, FileText, Send } from 'lucide-react';
import Button from '../../../components/ui/Button';
import EmptyState from '../../../components/EmptyState';

const ReviewCycleManagement = ({ employee, reviewCycles, employees, performanceData, onReviewCreate }) => {
  const [activeTab, setActiveTab] = useState('cycles');

  const getReviewStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 border-green-200';
      case 'draft': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'closed': return 'text-gray-600 bg-gray-100 border-gray-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getReviewStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle2;
      case 'draft': return FileText;
      case 'closed': return Clock;
      default: return AlertCircle;
    }
  };

  // Calculate review cycle statistics
  const cycleStats = {
    total: reviewCycles?.length || 0,
    active: reviewCycles?.filter(c => c?.status === 'active')?.length || 0,
    draft: reviewCycles?.filter(c => c?.status === 'draft')?.length || 0,
    closed: reviewCycles?.filter(c => c?.status === 'closed')?.length || 0
  };

  // Generate review templates
  const reviewTemplates = [
    {
      id: 'quarterly',
      name: 'Quarterly Performance Review',
      description: 'Comprehensive quarterly assessment',
      duration: '3 months',
      sections: ['Goals', 'Performance', 'Development', '360 Feedback']
    },
    {
      id: 'annual',
      name: 'Annual Performance Review',
      description: 'Complete yearly performance evaluation',
      duration: '12 months', 
      sections: ['Goals', 'Performance', 'Development', '360 Feedback', 'Compensation Review']
    },
    {
      id: 'probation',
      name: 'Probation Review',
      description: 'New employee probation assessment',
      duration: '3-6 months',
      sections: ['Onboarding', 'Performance', 'Feedback', 'Decision']
    }
  ];

  const upcomingReviews = employee ? [
    {
      employeeId: employee?.id,
      employeeName: `${employee?.firstName} ${employee?.lastName}`,
      reviewType: 'Quarterly Review',
      dueDate: '2024-12-30',
      status: 'pending',
      progress: 0
    }
  ] : [
    {
      employeeId: 'emp1',
      employeeName: 'Sample Employee 1',
      reviewType: 'Annual Review',
      dueDate: '2024-12-15',
      status: 'in_progress',
      progress: 75
    },
    {
      employeeId: 'emp2',
      employeeName: 'Sample Employee 2',
      reviewType: 'Quarterly Review',
      dueDate: '2024-12-20',
      status: 'pending',
      progress: 0
    }
  ];

  return (
    <div className="space-y-6">
      {/* Review Management Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 text-blue-600 mr-2" />
            Review Cycle Management
            {employee && <span className="text-base font-normal text-gray-600 ml-2">- {employee?.firstName} {employee?.lastName}</span>}
          </h2>
          <Button onClick={onReviewCreate} className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Create Review Cycle
          </Button>
        </div>

        {/* Review Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{cycleStats?.total}</div>
            <div className="text-sm text-blue-600">Total Cycles</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{cycleStats?.active}</div>
            <div className="text-sm text-green-600">Active</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{cycleStats?.draft}</div>
            <div className="text-sm text-yellow-600">Draft</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{cycleStats?.closed}</div>
            <div className="text-sm text-gray-600">Closed</div>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'cycles', label: 'Review Cycles' },
              { id: 'upcoming', label: 'Upcoming Reviews' },
              { id: 'templates', label: 'Templates' }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab?.id
                    ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Review Cycles Tab */}
          {activeTab === 'cycles' && (
            <div className="space-y-4">
              {reviewCycles?.length > 0 ? (
                reviewCycles?.map((cycle, index) => {
                  const StatusIcon = getReviewStatusIcon(cycle?.status);
                  const statusColor = getReviewStatusColor(cycle?.status);

                  return (
                    <div key={cycle?.id || index} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <StatusIcon className={`h-5 w-5 ${statusColor?.split(' ')?.[0]}`} />
                          <h3 className="text-lg font-semibold text-gray-900">
                            {cycle?.name}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColor}`}>
                            {cycle?.status?.toUpperCase()}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm">
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <span className="text-sm text-gray-500">Period</span>
                          <p className="font-medium">
                            {new Date(cycle?.periodStart)?.toLocaleDateString()} - {new Date(cycle?.periodEnd)?.toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Participants</span>
                          <p className="font-medium">All Employees</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Progress</span>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '65%' }}></div>
                            </div>
                            <span className="text-sm font-medium">65%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                          <Button variant="ghost" size="sm">
                            Edit Cycle
                          </Button>
                        </div>
                        {cycle?.status === 'active' && (
                          <Button size="sm">
                            <Send className="h-3 w-3 mr-1" />
                            Send Reminders
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyState
                  icon={Calendar}
                  title="No review cycles found"
                  description="Create your first review cycle to start managing employee performance reviews."
                  primaryAction={{
                    label: 'Create Review Cycle',
                    icon: Plus,
                    onClick: onReviewCreate
                  }}
                  secondaryAction={undefined}
                />
              )}
            </div>
          )}

          {/* Upcoming Reviews Tab */}
          {activeTab === 'upcoming' && (
            <div className="space-y-4">
              {upcomingReviews?.length > 0 ? (
                upcomingReviews?.map((review, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {review?.employeeName}
                        </h3>
                        <p className="text-sm text-gray-600">{review?.reviewType}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Due Date</p>
                        <p className="font-medium">{new Date(review?.dueDate)?.toLocaleDateString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          review?.status === 'completed' ? 'bg-green-100 text-green-800' :
                          review?.status === 'in_progress'? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {review?.status?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">
                          {review?.progress}% Complete
                        </span>
                      </div>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${review?.progress}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          View Review
                        </Button>
                        <Button variant="ghost" size="sm">
                          Send Reminder
                        </Button>
                      </div>
                      {review?.status === 'pending' && (
                        <Button size="sm">
                          Start Review
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState
                  icon={Clock}
                  title="No upcoming reviews"
                  description={employee 
                    ? `${employee?.firstName} has no upcoming reviews scheduled.`
                    : "No reviews are currently scheduled. Create a review cycle to schedule reviews."
                  }
                  primaryAction={{
                    label: 'Schedule Review',
                    icon: Calendar,
                    onClick: onReviewCreate
                  }}
                  secondaryAction={undefined}
                />
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewTemplates?.map((template) => (
                <div key={template?.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <FileText className="h-8 w-8 text-blue-600" />
                    <Button variant="ghost" size="sm">
                      Use Template
                    </Button>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {template?.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {template?.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Duration:</span>
                      <span className="font-medium">{template?.duration}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Sections:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template?.sections?.map((section, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {section}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="outline" size="sm" className="w-full">
                    Create from Template
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Review Process Guide */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-2">360-Degree Feedback Process</h3>
        <p className="text-green-800 text-sm mb-4">
          Comprehensive performance evaluation involving multiple perspectives for balanced assessment.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { step: '1', title: 'Self Assessment', description: 'Employee evaluates own performance' },
            { step: '2', title: 'Manager Review', description: 'Direct supervisor assessment' },
            { step: '3', title: 'Peer Feedback', description: 'Colleague evaluations' },
            { step: '4', title: 'Final Review', description: 'Consolidated feedback discussion' }
          ]?.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-green-100">
              <div className="text-center">
                <div className="text-lg font-bold text-green-600 mb-1">{item?.step}</div>
                <div className="text-sm font-medium text-green-900">{item?.title}</div>
                <div className="text-xs text-green-700 mt-1">{item?.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewCycleManagement;